import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────
// useProgressiveList — affichage progressif d'une liste.
//
// On garde toute la liste filtrée en mémoire (les filtres marché sont
// côté client), mais on n'affiche au départ que `pageSize` éléments.
// L'utilisateur charge la suite soit en cliquant sur « Charger plus »,
// soit en basculant en mode « défilement automatique » : un sentinelle
// invisible déclenche le chargement quand elle entre à l'écran.
//
// Le compteur se réinitialise dès que la liste change (nouveau filtre).
// ─────────────────────────────────────────────────────────────

export interface ProgressiveList<T> {
  /** Éléments à afficher pour l'instant. */
  visible: T[];
  /** Reste-t-il des éléments non affichés ? */
  hasMore: boolean;
  /** Nombre d'éléments encore masqués. */
  remaining: number;
  /** Nombre total d'éléments. */
  total: number;
  /** Charge la page suivante. */
  loadMore: () => void;
  /** À poser tout en bas de la liste : déclenche le chargement auto. */
  sentinelRef: React.RefObject<HTMLDivElement | null>;
}

export function useProgressiveList<T>(items: T[], pageSize = 12): ProgressiveList<T> {
  const [count, setCount] = useState(pageSize);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Nouveau jeu de données (filtre/recherche) → on repart de la première page.
  useEffect(() => {
    setCount(pageSize);
  }, [items, pageSize]);

  const loadMore = useCallback(() => {
    setCount((c) => Math.min(items.length, c + pageSize));
  }, [items.length, pageSize]);

  const hasMore = count < items.length;

  // Chargement automatique au défilement (sentinelle en bas de liste).
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: '240px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const visible = useMemo(() => items.slice(0, count), [items, count]);

  return {
    visible,
    hasMore,
    remaining: Math.max(0, items.length - count),
    total: items.length,
    loadMore,
    sentinelRef,
  };
}

export default useProgressiveList;